/**
 * 手役（陣形）の判定
 *
 * 手勢札 2 枚 ＋ 戦場札 3 枚 の計 5 枚で作る（オマハ系ルール）。
 * 戦場札だけで役を作ることはできない ＝ 自軍を出さずに勝つことはない。
 *
 * 【重要】役の順位は標準ポーカーの流用ではない。
 * 4〜5 兵種 × 13 ランクという構成のため出現確率が異なるので、
 * `packages/sim` の **全数列挙**（標本抽出ではない厳密な数え上げ）で得た
 * 頻度の逆順に並べている。
 * 順位を変えるときは FORMATION_ORDER だけを書き換えれば全体に反映される。
 * docs/06-賭けと軍配勝負.md §16
 */

import { RANK_COUNT, RANK_MAX, armIndexOf, rankOf, type CardId } from './cards';

export type Formation =
  | 'tanki' // 単騎駆   役なし
  | 'chouda' // 長蛇     同じ数 2 枚
  | 'kouyaku' // 衡軛     同じ数 2 枚 ×2
  | 'gyorin' // 魚鱗     同じ数 3 枚
  | 'gankou' // 雁行     数が 5 枚連続
  | 'kakuyoku' // 鶴翼     同じ兵種 5 枚
  | 'houen' // 方円     魚鱗＋長蛇
  | 'engetsu' // 偃月     同じ数 4 枚
  | 'houshi' // 鋒矢     同兵種で 5 枚連続
  | 'kurumagakari' // 車懸     同兵種の 9-10-11-12-13（最上位の鋒矢）
  | 'musou'; // 無双     同じ数 5 枚（5 兵種そろい踏み。鉄砲伝来後のみ成立）

export const FORMATION_LABEL: Readonly<Record<Formation, string>> = {
  tanki: '単騎駆',
  chouda: '長蛇',
  kouyaku: '衡軛',
  gyorin: '魚鱗',
  gankou: '雁行',
  kakuyoku: '鶴翼',
  houen: '方円',
  engetsu: '偃月',
  houshi: '鋒矢',
  kurumagakari: '車懸',
  musou: '無双',
};

export const FORMATION_READING: Readonly<Record<Formation, string>> = {
  tanki: 'たんきがけ',
  chouda: 'ちょうだ',
  kouyaku: 'こうやく',
  gyorin: 'ぎょりん',
  gankou: 'がんこう',
  kakuyoku: 'かくよく',
  houen: 'ほうえん',
  engetsu: 'えんげつ',
  houshi: 'ほうし',
  kurumagakari: 'くるまがかり',
  musou: 'むそう',
};

// --- 判定用の内部カテゴリ番号（順位とは独立） -------------------------------

const CAT_TANKI = 0;
const CAT_CHOUDA = 1;
const CAT_KOUYAKU = 2;
const CAT_GYORIN = 3;
const CAT_GANKOU = 4;
const CAT_KAKUYOKU = 5;
const CAT_HOUEN = 6;
const CAT_ENGETSU = 7;
const CAT_HOUSHI = 8;
const CAT_KURUMAGAKARI = 9;
const CAT_MUSOU = 10;

/** カテゴリ番号 → 役名 */
export const FORMATION_BY_CATEGORY: readonly Formation[] = [
  'tanki',
  'chouda',
  'kouyaku',
  'gyorin',
  'gankou',
  'kakuyoku',
  'houen',
  'engetsu',
  'houshi',
  'kurumagakari',
  'musou',
];

/**
 * 役の強さ。弱い順に 0 から。
 *
 * ★ この表がゲームバランスの根幹。`npm run sim:formations` の全数列挙で得た
 *   出現頻度の逆順そのもの。コメントの % は第II章（65 枚）の厳密値。
 *
 * 標準ポーカーとの違いは 2 点あり、いずれも 5 兵種目（鉄砲）が加わる影響：
 *
 *   1. 鶴翼（フラッシュ 0.0774%）が方円（フルハウス 0.1889%）より上位。
 *      兵種が 5 つになると同じ数を 3 枚＋2 枚そろえるのが易しくなる一方、
 *      1 兵種あたりの枚数は 13 のままなので、同兵種 5 枚は難しいまま。
 *      「鉄砲が戦場に加わって、同数を揃えるのが易しくなった」と説明できる。
 *
 *   2. 無双（同数 5 枚）が鋒矢と車懸の間に入る。鉄砲伝来後にしか成立しない。
 *
 * なお第I章（4 兵種 52 枚）だけは方円と鶴翼の頻度が逆転するが、
 * 順位表は通しで固定する。ゲーム中に順位が変わる方が混乱が大きいため。
 * 実害は「両者が正面衝突する確率」で測るべきで、第I章のヘッズアップでは
 * 手勢札 2 枚で 0.03%、4 枚でも 0.74% にとどまる（npm run sim:battle）。
 */
export const FORMATION_ORDER: Readonly<Record<Formation, number>> = {
  tanki: 0, //         48.2738%
  chouda: 1, //        43.2815%
  kouyaku: 2, //        5.1938%
  gyorin: 3, //         2.5969%
  gankou: 4, //         0.3400%
  houen: 5, //          0.1889%
  kakuyoku: 6, //       0.0774%
  engetsu: 7, //        0.0472%
  houshi: 8, //         0.0005%
  musou: 9, //          0.0002%
  kurumagakari: 10, //  0.0001%
};

/** カテゴリ番号 → 強さ順位（実行時の高速参照用） */
const ORDER_BY_CATEGORY: readonly number[] = FORMATION_BY_CATEGORY.map(
  (f) => FORMATION_ORDER[f],
);

/** 強さ順位 → 役名 */
export const FORMATION_BY_STRENGTH: readonly Formation[] = (() => {
  const out: Formation[] = [];
  for (const f of FORMATION_BY_CATEGORY) out[FORMATION_ORDER[f]] = f;
  return out;
})();

// --- 評価 -------------------------------------------------------------------

/** 同点処理のための桁数。ランクは 1..13 なので 4bit で足りる。 */
const TB_SHIFT = 4;
const STRENGTH_SCALE = 16 ** 5; // 1048576

/**
 * 5 枚の役の強さ。数値が大きいほど強い。
 *   score = 強さ順位 * 16^5 + 同点処理用のランク列
 * 単純な数値比較で勝敗が決まる。
 */
export type HandScore = number;

/** score から役名を取り出す */
export const formationOfScore = (score: HandScore): Formation =>
  FORMATION_BY_STRENGTH[Math.floor(score / STRENGTH_SCALE)]!;

// 判定用のスクラッチ（再入しないので使い回してよい）
const rankCount = new Int32Array(RANK_MAX + 1);
const armCount = new Int32Array(8);
const tbRanks = new Int32Array(5);

/**
 * 5 枚ちょうどの役を評価する。
 * ホットパスなので割り当てを行わない。
 */
export function evaluate5(a: CardId, b: CardId, c: CardId, d: CardId, e: CardId): HandScore {
  rankCount.fill(0);
  armCount.fill(0);

  const ra = rankOf(a);
  const rb = rankOf(b);
  const rc = rankOf(c);
  const rd = rankOf(d);
  const re = rankOf(e);
  rankCount[ra]!++;
  rankCount[rb]!++;
  rankCount[rc]!++;
  rankCount[rd]!++;
  rankCount[re]!++;
  armCount[armIndexOf(a)]!++;
  armCount[armIndexOf(b)]!++;
  armCount[armIndexOf(c)]!++;
  armCount[armIndexOf(d)]!++;
  armCount[armIndexOf(e)]!++;

  // 同数の多い順 → 数の大きい順で、同点処理用の並びを作る
  let n = 0;
  let maxCount = 0;
  let pairCount = 0;
  for (let need = 5; need >= 1; need--) {
    for (let rank = RANK_MAX; rank >= 1; rank--) {
      if (rankCount[rank] !== need) continue;
      if (need > maxCount) maxCount = need;
      if (need === 2) pairCount++;
      for (let k = 0; k < need; k++) tbRanks[n++] = rank;
    }
  }

  let cat: number;
  if (maxCount === 5) {
    cat = CAT_MUSOU;
  } else if (maxCount === 4) {
    cat = CAT_ENGETSU;
  } else if (maxCount === 3) {
    cat = pairCount === 1 ? CAT_HOUEN : CAT_GYORIN;
  } else if (maxCount === 2) {
    cat = pairCount === 2 ? CAT_KOUYAKU : CAT_CHOUDA;
  } else {
    // 5 枚とも異なる数。ここでのみ雁行・鶴翼が成立しうる。
    // （同兵種内で数は重複しないので、鶴翼なら必ず 5 枚とも異なる数になる）
    const isFlush =
      armCount[0] === 5 ||
      armCount[1] === 5 ||
      armCount[2] === 5 ||
      armCount[3] === 5 ||
      armCount[4] === 5;
    // tbRanks は降順に並んでいる。繰り上がりのない単純な連続のみ。
    const isStraight = tbRanks[0]! - tbRanks[4]! === 4;

    if (isFlush && isStraight) {
      cat = tbRanks[0] === RANK_MAX ? CAT_KURUMAGAKARI : CAT_HOUSHI;
    } else if (isFlush) {
      cat = CAT_KAKUYOKU;
    } else if (isStraight) {
      cat = CAT_GANKOU;
    } else {
      cat = CAT_TANKI;
    }
  }

  let tb = 0;
  for (let i = 0; i < 5; i++) tb = (tb << TB_SHIFT) | tbRanks[i]!;
  return ORDER_BY_CATEGORY[cat]! * STRENGTH_SCALE + tb;
}

/** 5 枚から 3 枚を選ぶ組み合わせ（戦場札用）。添字は昇順。 */
const BOARD_TRIPLES: ReadonlyArray<readonly [number, number, number]> = (() => {
  const out: Array<[number, number, number]> = [];
  for (let i = 0; i < 5; i++)
    for (let j = i + 1; j < 5; j++) for (let k = j + 1; k < 5; k++) out.push([i, j, k]);
  return out;
})();

/**
 * 手勢札から 2 枚、戦場札から 3 枚を選ぶ最良の組み合わせを返す。
 *
 * 手勢札は 2〜4 枚（投入兵で決まる。docs/06 §4）。
 * 戦場札は 5 枚ちょうど。
 */
export function bestHand(hole: readonly CardId[], board: readonly CardId[]): HandScore {
  if (hole.length < 2) throw new RangeError(`hole must have >= 2 cards, got ${hole.length}`);
  if (board.length !== 5) throw new RangeError(`board must have 5 cards, got ${board.length}`);

  let best = -1;
  for (let i = 0; i < hole.length; i++) {
    for (let j = i + 1; j < hole.length; j++) {
      const h1 = hole[i]!;
      const h2 = hole[j]!;
      for (const [x, y, z] of BOARD_TRIPLES) {
        const score = evaluate5(h1, h2, board[x]!, board[y]!, board[z]!);
        if (score > best) best = score;
      }
    }
  }
  return best;
}

/**
 * 役の段階補正。
 *
 * 【警告】常設の補正には使わないこと。実測で、防御側に +1 するだけで
 * 勝率が 50.6% → 82.8% に飛ぶ（npm run sim:battle）。
 * 城・武将能力・鉄砲・人質はすべて手勢札の枚数（holeCardCount）で表現する。
 * 残してあるのは下剋上のような一撃の逆転のためで、
 * マスタデータでの使用は validate:data が検出して失敗させる。
 * docs/06 §4
 */
export function applyFormationMod(score: HandScore, steps: number): HandScore {
  if (steps === 0) return score;
  const strength = Math.floor(score / STRENGTH_SCALE);
  const tb = score % STRENGTH_SCALE;
  const maxStrength = FORMATION_BY_STRENGTH.length - 1;
  const next = Math.min(maxStrength, Math.max(0, strength + steps));
  return next * STRENGTH_SCALE + tb;
}

/**
 * 役の強弱を完全に反転させる（下剋上。docs/06 §6）。
 * 単騎駆が車懸に勝つようになる。
 *
 * 反転するのは **役の順位だけ** で、同じ役どうしの同点処理（数の大小）は
 * そのまま保つ。ここを一緒に反転させると「弱い数の長蛇が強い数の長蛇に勝つ」
 * ことになり、大富豪の革命とは別物になってしまう。
 */
export function reverseFormation(score: HandScore): HandScore {
  const strength = Math.floor(score / STRENGTH_SCALE);
  const tb = score % STRENGTH_SCALE;
  const maxStrength = FORMATION_BY_STRENGTH.length - 1;
  return (maxStrength - strength) * STRENGTH_SCALE + tb;
}

/** 手役の内訳（UI・ログ用。ホットパスでは使わない） */
export interface HandDetail {
  readonly score: HandScore;
  readonly formation: Formation;
  readonly label: string;
}

export function describeHand(score: HandScore): HandDetail {
  const formation = formationOfScore(score);
  return { score, formation, label: FORMATION_LABEL[formation] };
}

/** ランク数の定数を再輸出（sim 側の検算用） */
export { RANK_COUNT };
